import { useCallback, useEffect, useRef } from 'react';

type Subscription = (e: KeyboardEvent) => void;

class ModalEventObserver {
  subscriptions: Subscription[] = [];

  constructor() {
    const getSubscription = (evt: KeyboardEvent) => {
      return this.subscriptions[this.subscriptions.length - 1]?.(evt);
    };

    document.addEventListener('keydown', getSubscription, false);
  }

  addSubscription(callback: Subscription) {
    this.subscriptions.push(callback);
  }

  removeSubscription(callback: Subscription) {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
  }
}

const modalEventObserver = new ModalEventObserver();

export const useModalEventObserver = (
  closeCallback: (event: KeyboardEvent) => void,
  options: {
    condition?: boolean;
    ignoreEscButton?: boolean;
  } = {},
) => {
  const ref = useRef(closeCallback);

  ref.current = closeCallback;

  const handleClose = useCallback(
    (e: KeyboardEvent) => {
      if (options.ignoreEscButton) {
        return;
      }

      ref.current(e);
    },
    [options.ignoreEscButton],
  );

  useEffect(() => {
    if (options.condition) {
      return;
    }

    modalEventObserver.addSubscription(handleClose);

    return () => {
      modalEventObserver.removeSubscription(handleClose);
    };
  }, [handleClose, options.condition]);

  useEffect(() => {
    if (options.condition === undefined) {
      return;
    }

    if (options.condition) {
      modalEventObserver.addSubscription(handleClose);
    } else {
      modalEventObserver.removeSubscription(handleClose);
    }
  }, [handleClose, options.condition]);
};
